import Link from "next/link";
import React, { useMemo } from "react";
import {
  Container,
  Card,
  CardBody,
  CardHeader,
  Spinner,
  Alert,
  Row,
  Col,
  Table,
} from "reactstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import useApi from "~/hooks/use-api";
import { Organization } from "../orgs";

// Data interfaces
interface StrategyData {
  strategy: string;
  shown_count: number;
  order_count: number;
  order_conversion_percentage: number;
  avg_order_value: number;
}

interface OrgData {
  organization_uid: string;
  strategy: string;
  shown_count: number;
  order_count: number;
  order_conversion_percentage: number;
}

interface TimeSeriesData {
  date: string;
  ltr_cart_conversion_percentage: number | null;
  random_cart_conversion_percentage: number | null;
  tag_cart_conversion_percentage: number | null;
  ltr_org_cart_conversion_percentage: number | null;
}

const STRATEGY_COLORS: Record<string, string> = {
  ltr: "#22c55e",
  random: "#3b82f6",
  tag: "#f59e0b",
  "ltr-org": "#a855f7",
};

const STRATEGY_LABELS: Record<string, string> = {
  ltr: "LTR",
  random: "RANDOM",
  tag: "TAG",
  "ltr-org": "LTR-ORG",
};

function StrategyCard({ strategy }: { strategy: StrategyData }) {
  const strategyType = strategy.strategy;
  const color = STRATEGY_COLORS[strategyType] || "#666";
  const label = STRATEGY_LABELS[strategyType] || strategyType.toUpperCase();

  return (
    <Card>
      <CardBody>
        <div className="d-flex align-items-center mb-2">
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: color,
              marginRight: 8,
            }}
          />
          <h5 className="mb-0">{label}</h5>
        </div>
        <div className="mb-2">
          <small className="text-muted">Total Shown</small>
          <div className="h4">{strategy.shown_count.toLocaleString()}</div>
        </div>
        <div className="mb-2">
          <small className="text-muted">Order Conversion</small>
          <div className="h6">
            {strategy.order_conversion_percentage.toFixed(2)}%
          </div>
        </div>
        <div className="mb-2">
          <small className="text-muted">Avg Order Value</small>
          <div className="h6">${strategy.avg_order_value.toFixed(2)}</div>
        </div>
        <div>
          <small className="text-muted">{strategy.order_count} orders</small>
        </div>
      </CardBody>
    </Card>
  );
}

export default function UpsellsDashboard() {
  // Fetch all data sources
  const {
    data: strategyData,
    error: strategyError,
    isValidating: strategyLoading,
  } = useApi<StrategyData[]>("/api/bi/upsells_strategy", {
    revalidateOnFocus: false,
    refreshInterval: 300_000,
  });

  const {
    data: orgData,
    error: orgError,
    isValidating: orgLoading,
  } = useApi<OrgData[]>("/api/bi/upsells_by_org", {
    revalidateOnFocus: false,
    refreshInterval: 300_000,
  });

  const {
    data: timeSeriesData,
    error: timeSeriesError,
    isValidating: timeSeriesLoading,
  } = useApi<TimeSeriesData[]>("/api/bi/upsells_conversion_time", {
    revalidateOnFocus: false,
    refreshInterval: 300_000,
  });

  const { data: organizations, isValidating: orgsLoading } = useApi<
    Organization[]
  >("/api/organization?includeDisabled=true", {});

  const organizationsMap = useMemo(() => {
    if (!organizations) return new Map<string, Organization>();
    return new Map(organizations.map((org) => [org.uid, org]));
  }, [organizations]);

  const chartData = useMemo(() => {
    if (!timeSeriesData) return [];

    const window = 3;
    const result = [];

    for (let i = 0; i < timeSeriesData.length; i++) {
      const windowStart = Math.max(0, i - window + 1);
      let ltrSum = 0;
      let ltrCount = 0;
      let randomSum = 0;
      let randomCount = 0;
      let tagSum = 0;
      let tagCount = 0;
      let ltrOrgSum = 0;
      let ltrOrgCount = 0;

      for (let windowIndex = windowStart; windowIndex <= i; windowIndex++) {
        const dataPoint = timeSeriesData[windowIndex];
        if (dataPoint.ltr_cart_conversion_percentage !== null) {
          ltrSum += dataPoint.ltr_cart_conversion_percentage;
          ltrCount++;
        }
        if (dataPoint.random_cart_conversion_percentage !== null) {
          randomSum += dataPoint.random_cart_conversion_percentage;
          randomCount++;
        }
        if (dataPoint.tag_cart_conversion_percentage !== null) {
          tagSum += dataPoint.tag_cart_conversion_percentage;
          tagCount++;
        }
        if (dataPoint.ltr_org_cart_conversion_percentage !== null) {
          ltrOrgSum += dataPoint.ltr_org_cart_conversion_percentage;
          ltrOrgCount++;
        }
      }

      const ltrAvg = ltrCount > 0 ? ltrSum / ltrCount : null;
      const randomAvg = randomCount > 0 ? randomSum / randomCount : null;
      const tagAvg = tagCount > 0 ? tagSum / tagCount : null;
      const ltrOrgAvg = ltrOrgCount > 0 ? ltrOrgSum / ltrOrgCount : null;

      if (
        ltrAvg !== null ||
        randomAvg !== null ||
        tagAvg !== null ||
        ltrOrgAvg !== null
      ) {
        result.push({
          date: new Date(timeSeriesData[i].date).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
          }),
          fullDate: timeSeriesData[i].date,
          ltr: ltrAvg,
          random: randomAvg,
          tag: tagAvg,
          "ltr-org": ltrOrgAvg,
        });
      }
    }

    return result;
  }, [timeSeriesData]);

  const dataRange = useMemo(() => {
    if (!timeSeriesData || timeSeriesData.length === 0) return null;

    let earliest = new Date(timeSeriesData[0].date);
    let latest = new Date(timeSeriesData[0].date);

    for (let i = 1; i < timeSeriesData.length; i++) {
      const currentDate = new Date(timeSeriesData[i].date);
      if (currentDate < earliest) earliest = currentDate;
      if (currentDate > latest) latest = currentDate;
    }

    const formatDate = (date: Date) =>
      date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });

    return `${formatDate(earliest)} - ${formatDate(latest)}`;
  }, [timeSeriesData]);

  const topOrgs = useMemo(() => {
    if (!orgData || !organizationsMap) return [];

    const enrichedData = [];
    for (let i = 0; i < orgData.length; i++) {
      const orgDataPoint = orgData[i];
      const org = organizationsMap.get(orgDataPoint.organization_uid);
      enrichedData.push({
        organization_uid: orgDataPoint.organization_uid,
        strategy: orgDataPoint.strategy,
        shown_count: orgDataPoint.shown_count,
        order_count: orgDataPoint.order_count,
        order_conversion_percentage: orgDataPoint.order_conversion_percentage,
        org_name: org?.name || orgDataPoint.organization_uid,
      });
    }

    enrichedData.sort(
      (a, b) =>
        a.org_name.localeCompare(b.org_name) ||
        a.strategy.localeCompare(b.strategy)
    );

    return enrichedData.slice(0, 50);
  }, [orgData, organizationsMap]);

  const isLoading =
    strategyLoading || orgLoading || timeSeriesLoading || orgsLoading;
  const error = strategyError || orgError || timeSeriesError;

  return (
    <Container className="py-4">
      <div className="pb-2">
        <h3 style={{ display: "inline" }} className="mb-4">
          Upsells Performance Dashboard
        </h3>
        <small style={{ display: "inline" }} className="text-muted px-2">
          Refreshed daily • Data range: {dataRange}
        </small>
      </div>

      {error && (
        <Alert color="danger">
          Error loading dashboard data: {error.message || "Unknown error"}
        </Alert>
      )}

      {isLoading && !strategyData && (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading dashboard data...</p>
        </div>
      )}

      {strategyData && (
        <>
          <Row className="mb-4">
            {strategyData.map((strategy) => (
              <Col key={strategy.strategy} md={3} className="mb-3">
                <StrategyCard strategy={strategy} />
              </Col>
            ))}
          </Row>

          <Card className="mb-4">
            <CardHeader>
              <h5 className="mb-0">
                Order Conversion Trends (3-Day Rolling Average)
              </h5>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart
                  data={chartData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 40 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" height={60} />
                  <YAxis
                    label={{
                      value: "Conversion Rate (%)",
                      angle: -90,
                      position: "left",
                    }}
                    tickFormatter={(value) => value.toFixed(2)}
                  />
                  <Tooltip formatter={(value: number) => value.toFixed(2)} />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="ltr"
                    stroke={STRATEGY_COLORS.ltr}
                    strokeWidth={2}
                    name="LTR"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="random"
                    stroke={STRATEGY_COLORS.random}
                    strokeWidth={2}
                    name="RANDOM"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="tag"
                    stroke={STRATEGY_COLORS.tag}
                    strokeWidth={2}
                    name="TAG"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="ltr-org"
                    stroke={STRATEGY_COLORS["ltr-org"]}
                    strokeWidth={2}
                    name="LTR-ORG"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h5 className="mb-0">Top 50 Strategies by Org</h5>
            </CardHeader>
            <CardBody>
              {topOrgs.length === 0 ? (
                <div className="text-center text-muted py-3">
                  No organization data available
                </div>
              ) : (
                <Table hover responsive>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Organization</th>
                      <th>Strategy</th>
                      <th className="text-end">Shown</th>
                      <th className="text-end">Orders</th>
                      <th className="text-end">Order Conv. %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topOrgs.map((org, rank) => {
                      const strategyColor =
                        STRATEGY_COLORS[org.strategy] || "#666";
                      const strategyLabel =
                        STRATEGY_LABELS[org.strategy] ||
                        org.strategy.toUpperCase();

                      return (
                        <tr key={`${org.organization_uid}:${org.strategy}`}>
                          <td>{rank + 1}</td>
                          <td>
                            <Link href={`/orgs/${org.organization_uid}`}>
                              {org.org_name}
                            </Link>
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-block",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: strategyColor,
                                marginRight: 6,
                              }}
                            />
                            {strategyLabel}
                          </td>
                          <td className="text-end">
                            {org.shown_count.toLocaleString()}
                          </td>
                          <td className="text-end">{org.order_count}</td>
                          <td className="text-end">
                            {org.order_conversion_percentage.toFixed(2)}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </Container>
  );
}
